import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  Card,
  CardContent,
  Grid,
  Divider,
  Button,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  CloseOutlined,
  AccountBalanceWalletOutlined,
  ReceiptLongOutlined,
  AddCardOutlined,
  CurrencyExchangeOutlined,
  TuneOutlined,
  VerifiedUserOutlined,
  InfoOutlined,
  CreditScoreOutlined,
  CalculateOutlined,
  LockResetOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import WalletBalance, { formatKES } from "./WalletBalance";
import WalletTopUp from "./WalletTopUp";
import WalletLedger from "./WalletLedger";
import WalletWithdrawal from "./WalletWithdrawal";
import OverdraftPanel from "./OverdraftPanel.js";
import ExitReactivation from "./ExitReactivation.js";
import ChargePreview from "./ChargePreview.js";
import { companyManagementUrl } from "../../config";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import {
  walletQuery,
  WALLET_QUERY,
  OVERDRAFT_FACILITY_QUERY,
  WALLET_LEDGER_QUERY,
  createWalletClient,
  SABABISHA_SHORT_CODE_QUERY,
} from "../../data/WalletService/walletGraphQL";

const WalletManagement = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // Resolve current active company and user credentials from decodedToken and database
  const [companyInfo, setCompanyInfo] = useState({
    id: null,
    name: "",
    userPhone: "",
  });
  const [paybillNumber, setPaybillNumber] = useState("9617815");

  // Fetch the Sababisha Global Paybill shortcode dynamically
  useEffect(() => {
    try {
      const walletClient = createWalletClient();
      walletClient
        .query({ query: SABABISHA_SHORT_CODE_QUERY })
        .then((res) => {
          const sc = res?.data?.sababishaShortCode;
          if (sc) setPaybillNumber(sc);
        })
        .catch((err) => {
          console.warn("Could not fetch Sababisha shortcode:", err?.message);
        });
    } catch (e) {
      console.warn("Failed to initialize wallet client for shortcode:", e?.message);
    }
  }, []);

  useEffect(() => {
    async function resolveCompany() {
      try {
        const raw = localStorage.getItem("decodedToken");
        if (raw) {
          const decoded = JSON.parse(raw);
          const resolvedId = parseInt(
            decoded?.CompanyId || decoded?.companyId || decoded?.CompanyID || 1,
            10
          );
          const resolvedName =
            decoded?.Company || decoded?.CompanyName || "";
          const resolvedPhone = "0712345678";

          setCompanyInfo({
            id: resolvedId,
            name: resolvedName,
            userPhone: resolvedPhone,
          });

          // Fetch exact company name from database if missing or generic
          if (resolvedId) {
            try {
              const compRes = await fetchJsonDynamic(
                "companies",
                null,
                null,
                companyManagementUrl.uri
              );
              if (compRes?.results) {
                const parsed = JSON.parse(compRes.results);
                if (Array.isArray(parsed)) {
                  const matched = parsed.find((c) => c && c.Id === resolvedId);
                  if (matched && (matched.CompanyName || matched.Name)) {
                    setCompanyInfo((prev) => ({
                      ...prev,
                      name: matched.CompanyName || matched.Name,
                    }));
                  }
                }
              }
            } catch (ce) {
              console.warn("Could not resolve company name:", ce);
            }
          }
        }
      } catch (e) {
        console.warn("Failed reading decodedToken:", e);
      }
    }
    resolveCompany();
  }, []);

  const [walletData, setWalletData] = useState({
    companyId: null,
    availableBalance: 0.0,
    escrowedBalance: 0.0,
    totalDeposited: 0.0,
    totalDisbursed: 0.0,
    totalFeesCharged: 0.0,
    totalRoyaltiesPaid: 0.0,
    lowBalanceThreshold: 0.0,
    isBelowThreshold: false,
    isActive: true,
  });

  const [feeConfig, setFeeConfig] = useState({
    feeType: "PERCENTAGE",
    feeValue: 0,
    minFee: 0,
    maxFee: 0,
    royaltyType: "PERCENTAGE",
    royaltyValue: 0,
    effectiveFrom: "",
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Dialog modals for quick actions from header or balance card
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);

  const fetchWallet = useCallback(async () => {
    if (!companyInfo.id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
      };

      // ── Primary: GraphQL wallet query ──────────────────────────────────
      let walletFetched = false;
      try {
        const gqlData = await walletQuery(WALLET_QUERY, { companyId: parseInt(companyInfo.id, 10) });
        if (gqlData?.wallet) {
          const w = gqlData.wallet;
          const avail = Number(w.availableBalance || 0);
          const minRet = Number(w.minimumRetainedBalance || 0);
          setWalletData((prev) => ({
            ...prev,
            companyId: w.companyId,
            availableBalance: avail,
            escrowedBalance: Number(w.escrowedBalance || 0),
            minRetainedBalance: minRet,
            withdrawableBalance: Math.max(0, avail - minRet),
            lowBalanceThreshold: minRet,
            isBelowThreshold: minRet > 0 && avail <= minRet,
            lifecycleState: (w.lifecycleState || "ACTIVE").toUpperCase(),
          }));
          walletFetched = true;
        }
      } catch (gqlErr) {
        console.warn("Wallet GQL query failed:", gqlErr);
      }

      // ── Supplemental: aggregate real lifetime metrics from Wallet Ledger ───
      try {
        const ledgerData = await walletQuery(WALLET_LEDGER_QUERY, {
          companyId: parseInt(companyInfo.id, 10),
          page: 1,
          pageSize: 500,
        });
        const entries = ledgerData?.walletLedger;
        if (Array.isArray(entries) && entries.length > 0) {
          let deposited = 0;
          let disbursed = 0;
          let fees = 0;
          let royalties = 0;

          entries.forEach((e) => {
            const amt = Math.abs(Number(e.amount || 0));
            const tx = (e.txType || "").toUpperCase();

            if (tx.includes("TOPUP") || tx.includes("TOP_UP")) {
              deposited += amt;
            } else if (
              tx.includes("DISBURSEMENT") ||
              tx.includes("ESCROW_COMMIT") ||
              (tx.includes("WITHDRAWAL") && !tx.includes("ROYALTY"))
            ) {
              disbursed += amt;
            } else if (
              tx.includes("FEE") ||
              tx.includes("CHARGE") ||
              tx.includes("ACTIVATION") ||
              tx.includes("EXIT")
            ) {
              fees += amt;
            } else if (tx.includes("ROYALTY")) {
              royalties += amt;
            }
          });

          setWalletData((prev) => ({
            ...prev,
            totalDeposited: deposited,
            totalDisbursed: disbursed,
            totalFeesCharged: fees,
            totalRoyaltiesPaid: royalties,
          }));
        }
      } catch (ledgerErr) {
        console.warn("Could not aggregate wallet ledger metrics:", ledgerErr);
      }

      // ── Supplemental: overdraft headroom via GraphQL ────────────
      try {
        const odData = await walletQuery(OVERDRAFT_FACILITY_QUERY, {
          companyId: parseInt(companyInfo.id, 10),
        });
        if (odData?.overdraftFacility) {
          const od = odData.overdraftFacility;
          const limit = Number(od.facilityLimit || 0);
          const drawn = Number(od.outstandingPrincipal || 0);
          const interest = Number(od.accruedInterest || 0);
          const odHeadroom = Math.max(0, limit - drawn - interest);
          setWalletData((prev) => ({
            ...prev,
            overdraftHeadroom: odHeadroom,
            overdraftApprovedLimit: limit,
          }));
        }
      } catch (extraErr) {
        console.warn("Could not query overdraft facility for headroom:", extraErr);
      }
    } catch (err) {
      console.warn("Failed to fetch wallet:", err);
    } finally {
      setLoading(false);
    }
  }, [companyInfo.id]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  const handleTopUpSuccess = () => {
    fetchWallet();
  };

  const handleWithdrawalSuccess = () => {
    fetchWallet();
  };

  const formattedCompanyCode = String(companyInfo.id).padStart(4, "0");

  return (
    <Box m={{ xs: "12px", md: "24px" }} sx={{ minWidth: 0, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Breadcrumb & Navigation */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.85rem" }}>
          <Link underline="hover" color="inherit" href="/dashboard">
            Dashboard
          </Link>
          <Link underline="hover" color="inherit" href="/company-settings">
            Company Management
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 600 }}>
            Company Wallet
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Screen Title & Header Info */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: colors.grey[100],
                fontFamily: "Outfit, Inter, sans-serif",
              }}
            >
              Company Wallet
            </Typography>
            <Chip
              icon={<VerifiedUserOutlined sx={{ fontSize: "16px !important", color: "#22bf72" }} />}
              label="Global Paybill Rail Active"
              size="small"
              sx={{
                backgroundColor: isDark ? "rgba(34, 191, 114, 0.15)" : "#e8f8f0",
                color: "#22bf72",
                fontWeight: 700,
                fontSize: "0.75rem",
                borderRadius: 1.5,
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: colors.grey[400], mt: 0.5 }}>
            Centralized Safaricom payment rail for <strong>{companyInfo.name}</strong>
          </Typography>
        </Box>

        {/* Header Action Buttons */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={() => setTopUpModalOpen(true)}
            startIcon={<AddCardOutlined />}
            sx={{
              backgroundColor: "#22bf72",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              "&:hover": { backgroundColor: "#1b995b" },
            }}
          >
            Deposit / Top Up
          </Button>
          <Button
            variant="outlined"
            onClick={() => setWithdrawModalOpen(true)}
            startIcon={<CurrencyExchangeOutlined />}
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
              color: colors.grey[100],
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Withdraw Funds
          </Button>
        </Box>
      </Box>

      {/* Hero Wallet Balance Card */}
      <WalletBalance
        walletData={walletData}
        loading={loading}
        onRefresh={fetchWallet}
        onOpenTopUp={() => setTopUpModalOpen(true)}
        onOpenWithdraw={() => setWithdrawModalOpen(true)}
        paybillNumber={paybillNumber}
        overdraftApprovedLimit={walletData.overdraftApprovedLimit || 0}
      />

      {/* Main Tabbed Sections */}
      <Box sx={{ mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 3,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
            },
          }}
        >
          <Tab
            icon={<ReceiptLongOutlined />}
            iconPosition="start"
            label="Transaction Ledger"
          />
          <Tab
            icon={<CreditScoreOutlined />}
            iconPosition="start"
            label="Overdraft Facility"
          />
          <Tab
            icon={<CalculateOutlined />}
            iconPosition="start"
            label="Charge Preview"
          />
          <Tab
            icon={<AddCardOutlined />}
            iconPosition="start"
            label="Top Up Wallet"
          />
          <Tab
            icon={<CurrencyExchangeOutlined />}
            iconPosition="start"
            label="Withdraw Funds"
          />
          <Tab
            icon={<LockResetOutlined />}
            iconPosition="start"
            label="Lifecycle & Exit"
          />
        </Tabs>

        {/* Tab 0: Ledger */}
        {activeTab === 0 && (
          <WalletLedger companyId={companyInfo.id} />
        )}

        {/* Tab 1: Overdraft Facility */}
        {activeTab === 1 && (
          <OverdraftPanel companyId={companyInfo.id} />
        )}

        {/* Tab 2: Charge Transparency Preview */}
        {activeTab === 2 && (
          <ChargePreview
            companyId={companyInfo.id}
            availableBalance={walletData.availableBalance}
            overdraftHeadroom={walletData.overdraftHeadroom || 0}
          />
        )}

        {/* Tab 3: Top Up Inline Form */}
        {activeTab === 3 && (
          <WalletTopUp
            companyId={companyInfo.id}
            userPhone={companyInfo.userPhone}
            onTopUpSuccess={handleTopUpSuccess}
            paybillNumber={paybillNumber}
          />
        )}

        {/* Tab 4: Withdrawal Inline Form */}
        {activeTab === 4 && (
          <WalletWithdrawal
            companyId={companyInfo.id}
            availableBalance={walletData.availableBalance}
            onWithdrawalSubmitted={handleWithdrawalSuccess}
          />
        )}

        {/* Tab 5: Lifecycle, Dormancy & Exit  */}
        {activeTab === 5 && (
          <ExitReactivation
            companyId={companyInfo.id}
            onStateChange={fetchWallet}
          />
        )}
      </Box>

      {/* TOP-UP MODAL DIALOG */}
      <Dialog
        open={topUpModalOpen}
        onClose={() => setTopUpModalOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AddCardOutlined sx={{ color: "#22bf72" }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Top Up Company Wallet
            </Typography>
          </Box>
          <IconButton onClick={() => setTopUpModalOpen(false)}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <WalletTopUp
            companyId={companyInfo.id}
            userPhone={companyInfo.userPhone}
            onTopUpSuccess={handleTopUpSuccess}
            onClose={() => setTopUpModalOpen(false)}
            paybillNumber={paybillNumber}
          />
        </DialogContent>
      </Dialog>

      {/* WITHDRAWAL MODAL DIALOG */}
      <Dialog
        open={withdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CurrencyExchangeOutlined sx={{ color: "#1766a2" }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Withdraw Wallet Funds
            </Typography>
          </Box>
          <IconButton onClick={() => setWithdrawModalOpen(false)}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <WalletWithdrawal
            companyId={companyInfo.id}
            availableBalance={walletData.availableBalance}
            onWithdrawalSubmitted={handleWithdrawalSuccess}
            onClose={() => setWithdrawModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WalletManagement;
